import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import customerService from '../../../../services/customerService';
import purchaseErrorMessage from '../../../../utils/purchaseErrors';
import { useCustomerAuth } from '../../../../contexts/CustomerAuthContext';
import SubscriptionTerms from '../../../../components/customer/SubscriptionTerms';
import LoadingSpinner from '../../../../components/core/LoadingSpinner';
import { formatCurrency } from '../../../../utils/currency';
import { PublicProduct } from '../../../../types/product';
import { PurchasableProduct } from '../../../../types/customer';
import { planChargeForViewer } from '../../../../utils/planPricing';

/**
 * The last step of booking an option that is sold as a PLAN.
 *
 * Someone picks the plan option, chooses who they are seeing and when, and
 * arrives here to subscribe. That order is deliberate: this used to be a
 * sign-in wall thrown up the instant the option was tapped, which asked a
 * visitor to make an account before they had seen a single available time.
 *
 * IT SUBSCRIBES; IT DOES NOT CHARGE A ONE-OFF. Choosing the plan option means
 * joining that plan, and this booking then comes out of its allowance. Billing
 * a single month's fee with nothing recurring behind it would take the money
 * and leave them holding nothing.
 *
 * NO ACCOUNT NEEDED. The address is the one they just typed on the booking
 * form, and the receipt the purchase returns is what lets the booking go
 * through — see `generatePurchaseGrantToken` on the server. An email inviting
 * them to set a password follows, for managing the plan later.
 *
 * SOMEONE ALREADY ON A PLAN IS MOVED, NOT SIGNED UP AGAIN. Choosing a dearer
 * option when you hold a cheaper one is an UPGRADE: the live subscription
 * changes price, Stripe prorates, and the difference is billed on the spot
 * against the card already on file. Starting a second subscription instead —
 * which is what `purchaseSubscription` does — would leave them paying for both
 * plans at once, forever, for a booking they made once.
 *
 * DIRECT CHARGES: the subscription lives on the HOST'S connected account, so
 * stripe.js must be loaded with `stripeAccount` or the PaymentElement fails
 * with "no such payment intent". Same requirement as the Pay Now screen.
 */

interface PlanCheckoutScreenProps {
  /**
   * What actually gets subscribed to — the plan the SERVER named when it
   * refused the booking. `product` below is for display; this is the decision,
   * and keeping them separate means the screen can never charge for one plan
   * while describing another.
   */
  productId: number;
  /** The plan option they chose, for what is shown on screen. */
  product: PublicProduct;
  /** From the booking form. Ignored server-side when they are signed in. */
  email: string;
  /** When the slot hold lapses — shown as a countdown, and enforced server-side. */
  expiresAt: string;
  /** Named on the recurring-purchase disclosure. */
  sellerName?: string | null;
  /**
   * Whose wallet to look in. A plan is held PER ORGANIZATION, so without this
   * the screen cannot tell an upgrade from a first purchase.
   */
  organizationId?: number | null;
  /**
   * The plan is live — the caller confirms the held booking.
   *
   * Carries WHAT happened, so the confirmation page can say it. A first
   * subscription and an upgrade are different events and reading "your plan is
   * active" after moving up tells someone nothing they did not already know.
   */
  onPurchased: (outcome?: PlanPurchaseOutcome) => void;
  /** Backed out. The caller returns them to the form with nothing charged. */
  onCancel: () => void;
}

/** What the checkout did, for the confirmation page to report. */
export interface PlanPurchaseOutcome {
  /** True when a plan they already held was moved up, not started. */
  upgraded: boolean;
  /** The monthly difference on an upgrade, the full price otherwise. */
  amount: number | null;
  currency: string;
}

interface CheckoutInit {
  clientSecret: string;
  subscriptionId: string;
  amount: number;
  currency: string;
  publishableKey: string;
  connectedAccountId: string;
}

const PlanCheckoutScreen: React.FC<PlanCheckoutScreenProps> = ({
  productId,
  product,
  email,
  expiresAt,
  sellerName,
  organizationId,
  onPurchased,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { isSignedIn, refresh, walletFor } = useCustomerAuth();

  const [init, setInit] = useState<CheckoutInit | null>(null);
  const [starting, setStarting] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);

  /**
   * The plan they hold with this host, when it is not the one being bought.
   *
   * Its presence is what turns this screen from a sign-up into a CHANGE: no
   * card form, no second subscription, just the difference.
   */
  const heldPlan = walletFor(organizationId)?.subscription ?? null;
  const changeFrom = heldPlan && heldPlan.productId !== productId ? heldPlan : null;

  /** What this actually costs them — the difference when moving up. */
  const planCharge = planChargeForViewer(
    product.price ?? null,
    changeFrom?.amount ?? null
  );

  /**
   * The slot is held for a while, and saying so is the point.
   *
   * Without it the invitee has no idea their time is reserved, and no idea it
   * will stop being reserved — the same reason the Pay Now screen counts down.
   */
  const [remainingMs, setRemainingMs] = useState<number>(() =>
    Math.max(0, new Date(expiresAt).getTime() - Date.now())
  );

  useEffect(() => {
    const id = setInterval(() => {
      const left = Math.max(0, new Date(expiresAt).getTime() - Date.now());
      setRemainingMs(left);
      if (left <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const expired = remainingMs <= 0;
  const mm = Math.floor(remainingMs / 60_000);
  const ss = Math.floor((remainingMs % 60_000) / 1000)
    .toString()
    .padStart(2, '0');

  /** SubscriptionTerms speaks the shop's product shape, not the booking page's. */
  const termsProduct = useMemo<PurchasableProduct>(
    () => ({
      id: product.id,
      kind: 'subscription',
      title: product.title,
      price: product.price ?? null,
      currency: product.currency,
      billingInterval: 'month',
      allowanceCount: product.allowanceCount ?? null,
      allowancePeriod: product.allowancePeriod ?? 'month',
    }),
    [product]
  );

  /**
   * Start the subscription as soon as this screen opens.
   *
   * Everything it needs was decided on earlier screens, so an extra "continue
   * to payment" button here would only be a step between two steps.
   */
  useEffect(() => {
    let cancelled = false;

    // A change needs no PaymentIntent: the card on file is charged the
    // proration. Running the purchase path here is the double-subscription bug.
    if (changeFrom) {
      setStarting(false);
      return;
    }

    const begin = async () => {
      try {
        const result = await customerService.purchaseSubscription(
          productId,
          isSignedIn ? undefined : email
        );
        if (cancelled) return;

        /**
         * No secret means one of two very different things, and treating them
         * alike is what made a failed purchase look like a success. Only
         * Stripe's own status may decide it.
         */
        if (!result.clientSecret) {
          if (result.status === 'active' || result.status === 'trialing') {
            // Paid outright, no authentication step. The receipt is already
            // stored, so the booking can go straight through.
            await refresh();
            onPurchased();
            return;
          }
          setError(
            t(
              'booking:planCheckout.couldNotStart',
              'We could not start the payment. Nothing has been charged — please try again.'
            )
          );
          return;
        }

        if (!result.publishableKey) {
          setError(
            t(
              'booking:planCheckout.unavailable',
              'Payment is not available right now. Please contact the host.'
            )
          );
          return;
        }

        setInit({
          clientSecret: result.clientSecret,
          subscriptionId: result.subscriptionId,
          amount: result.amount,
          currency: result.currency,
          publishableKey: result.publishableKey,
          connectedAccountId: result.connectedAccountId,
        });
      } catch (err: any) {
        if (cancelled) return;
        setError(
          purchaseErrorMessage(
            err,
            t,
            t(
              'booking:planCheckout.startFailed',
              'Could not start the payment. Please try again.'
            )
          )
        );
      } finally {
        if (!cancelled) setStarting(false);
      }
    };

    begin();
    return () => {
      cancelled = true;
    };
    // Runs once for this plan. Re-running would create a second subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, !!changeFrom]);

  /**
   * Move the live subscription onto this plan.
   *
   * Stripe bills the prorated difference immediately against the card already
   * on file, which is why there is no payment form here — and why the booking
   * confirms the moment the change lands.
   */
  const applyChange = useCallback(async () => {
    if (!changeFrom || changing || expired) return;
    setChanging(true);
    setError(null);
    try {
      await customerService.changeSubscriptionPlan(changeFrom.id, productId);
      await refresh();
      onPurchased({
        upgraded: planCharge?.isUpgrade === true,
        amount: planCharge?.amount ?? null,
        currency: product.currency,
      });
    } catch (err: any) {
      setError(
        purchaseErrorMessage(
          err,
          t,
          t(
            'booking:planCheckout.changeFailed',
            'We could not change your plan. Nothing has been charged — please try again.'
          )
        )
      );
    } finally {
      setChanging(false);
    }
    // `expired` is read here for the same reason the payment form reads it: the
    // hold can lapse while this screen is open.
  }, [changeFrom, changing, expired, productId, planCharge, product.currency, refresh, onPurchased, t]);

  const stripePromise = useMemo<Promise<Stripe | null> | null>(
    () =>
      init
        ? loadStripe(init.publishableKey, { stripeAccount: init.connectedAccountId })
        : null,
    [init]
  );

  return (
    <div className="mx-auto max-w-xl rounded-lg border border-gray-200 bg-white p-6 shadow-md">
      <h2 className="mb-1 text-xl font-semibold text-gray-900">
        {changeFrom
          ? planCharge?.isUpgrade
            ? t('booking:planCheckout.upgradeTitle', 'Upgrade your plan to book this')
            : t('booking:planCheckout.changeTitle', 'Switch your plan to book this')
          : t('booking:planCheckout.title', 'Subscribe to book this')}
      </h2>
      <p className="mb-4 text-sm text-gray-600">
        {changeFrom
          ? t(
              'booking:planCheckout.upgradeSubtitle',
              'You are on {{plan}}. We will move you to this plan and charge only the difference — your booking is confirmed straight away.',
              { plan: changeFrom.productTitle || t('booking:planCheckout.yourPlan', 'your current plan') }
            )
          : t(
              'booking:planCheckout.subtitle',
              'This option is included in a plan. Start the plan and your booking is confirmed straight away.'
            )}
      </p>

      {/* What they are joining, what it includes, and — unmissably — that it
          RECURS.

          This is the screen where a recurring commitment is entered into, and
          the monthly figure was set in the same weight as the allowance line
          above it. Someone skimming could take it for the price of the booking
          they came for. The amount is now the largest thing in the box and
          says "every month" in words, not just a "/ month" suffix. */}
      <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm font-medium text-gray-900">{product.title}</p>
        <p className="mt-0.5 text-xs text-gray-500">
          {product.allowanceCount == null
            ? t('booking:points.planUnlimited', 'Unlimited meetings')
            : t('booking:points.planAllowance', '{{count}} meetings per {{period}}', {
                count: product.allowanceCount,
                period: t(`booking:period.${product.allowancePeriod ?? 'month'}`, 'month'),
              })}
        </p>
        {product.price != null && planCharge && (
          <div className="mt-3 border-t border-gray-200 pt-3">
            <p className="text-xs text-gray-500">
              {planCharge.isUpgrade
                ? t('booking:planCheckout.extraEveryMonth', 'Extra, every month')
                : t('booking:planCheckout.chargedEveryMonth', 'Charged every month')}
            </p>
            <p className="mt-0.5 text-2xl font-bold text-gray-900">
              {planCharge.isUpgrade ? '+' : ''}
              {formatCurrency(planCharge.amount, product.currency)}
              <span className="ml-1 text-base font-medium text-gray-500">
                {t('booking:planCheckout.perMonthSuffix', '/ month')}
              </span>
            </p>
            {/* The full price stays visible. An upgrade line that only ever
                shows the difference hides what the plan actually costs, which
                is the number on next month's statement. */}
            {planCharge.isUpgrade && (
              <p className="mt-1 text-xs text-gray-500">
                {t(
                  'booking:planCheckout.upgradeFullPrice',
                  '{{plan}} is {{price}} / month in total. You pay the difference for the rest of this month now.',
                  {
                    plan: product.title,
                    price: formatCurrency(Number(product.price), product.currency),
                  }
                )}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {t(
                'booking:planCheckout.recurringNote',
                'This repeats until you cancel. You can cancel any time.'
              )}
            </p>
          </div>
        )}
      </div>

      {/* The slot is reserved, and for how long. */}
      <div
        className={`mb-4 flex items-center justify-between rounded-md px-3 py-2 text-sm ${
          expired
            ? 'border border-red-200 bg-red-50 text-red-700'
            : 'border border-yellow-200 bg-yellow-50 text-yellow-800'
        }`}
      >
        <span>
          {expired
            ? t('booking:planCheckout.expired', 'Your reservation expired. Please pick another time.')
            : t('booking:planCheckout.holdingSlot', 'Your time slot is held for the next')}
        </span>
        {!expired && (
          <span className="font-mono font-semibold">
            {mm}:{ss}
          </span>
        )}
      </div>

      {starting && (
        <div className="flex flex-col items-center gap-3 py-10 text-gray-500">
          <LoadingSpinner size="lg" />
          <span className="text-sm">
            {t('booking:planCheckout.preparing', 'Preparing your payment…')}
          </span>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* A CHANGE takes no card details — the subscription already has one. */}
      {changeFrom && (
        <button
          type="button"
          onClick={applyChange}
          disabled={changing || expired}
          className="btn-primary mt-2 w-full"
        >
          {changing ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingSpinner /> {t('booking:planCheckout.processing', 'Processing…')}
            </span>
          ) : planCharge?.isUpgrade ? (
            t('booking:planCheckout.upgradeButton', 'Upgrade and book — +{{amount}} / month', {
              amount: formatCurrency(planCharge.amount, product.currency),
            })
          ) : (
            t('booking:planCheckout.changeButton', 'Switch plan and book')
          )}
        </button>
      )}

      {!changeFrom && init && stripePromise && (
        <Elements
          stripe={stripePromise}
          options={{ clientSecret: init.clientSecret, appearance: { theme: 'stripe' as const } }}
        >
          <PlanPaymentForm init={init} expired={expired} onPurchased={onPurchased} />
        </Elements>
      )}

      <div className="mt-4 text-center">
        <button type="button" className="btn-text-danger text-sm" onClick={onCancel}>
          {changeFrom
            ? t('booking:planCheckout.backChange', 'Go back without changing plan')
            : t('booking:planCheckout.back', 'Go back without subscribing')}
        </button>
      </div>

      {/* Below the button, in the same grey as any other fine print — a
          recurring purchase carries statutory disclosure in Japan. */}
      <SubscriptionTerms product={termsProduct} sellerName={sellerName} />
    </div>
  );
};

const PlanPaymentForm: React.FC<{
  init: CheckoutInit;
  /** The hold lapsed — taking money now would buy a slot we no longer reserve. */
  expired: boolean;
  onPurchased: () => void;
}> = ({ init, expired, onPurchased }) => {
  const { t } = useTranslation();
  const { refresh } = useCustomerAuth();
  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pay = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!stripe || !elements || !ready || submitting || expired) return;

      setSubmitting(true);
      setError(null);

      // Wrapped so `submitting` is ALWAYS released — a rejection here otherwise
      // leaves the button stuck at "Processing…" with no way back.
      try {
        const { error: stripeError } = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: window.location.href },
          redirect: 'if_required',
        });

        if (stripeError) {
          setError(
            stripeError.message ||
              t('booking:planCheckout.failed', 'Payment failed. Please try again.')
          );
          return;
        }

        /**
         * Stripe, not us, decides the plan is live — and this call is what
         * hands back the receipt the booking needs. Reporting success without
         * it would send them on to a booking the server will refuse.
         */
        const { status } = await customerService.syncSubscription(init.subscriptionId);
        if (status !== 'active' && status !== 'trialing') {
          setError(
            t(
              'booking:planCheckout.stillProcessing',
              'Your payment is still processing. Please try again in a moment.'
            )
          );
          return;
        }

        await refresh();
        onPurchased();
      } catch (err: any) {
        setError(
          err?.message || t('booking:planCheckout.failed', 'Payment failed. Please try again.')
        );
      } finally {
        setSubmitting(false);
      }
    },
    // `expired` belongs here: the hold can lapse while this form is open, and a
    // callback holding the old value would take money for a slot we stopped
    // reserving a moment ago.
    [stripe, elements, ready, submitting, expired, init.subscriptionId, refresh, onPurchased, t]
  );

  return (
    <form onSubmit={pay}>
      {/* Always mounted and visible, never display:none — mounting a
          PaymentElement hidden lets onReady fire before the fields paint, which
          enables the button over an empty-looking form. The spinner overlays it
          instead. */}
      <div className="relative min-h-[3rem]">
        {!ready && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/90 py-12 text-gray-500">
            <LoadingSpinner size="lg" />
            <span className="text-sm">
              {t('booking:planCheckout.loadingForm', 'Loading secure payment form…')}
            </span>
          </div>
        )}
        <PaymentElement
          onReady={() => setReady(true)}
          onLoadError={() => {
            setReady(true);
            setError(t('booking:planCheckout.failed', 'Payment failed. Please try again.'));
          }}
        />
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || !elements || !ready || submitting || expired}
        className="btn-primary mt-4 w-full"
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-2">
            <LoadingSpinner />{' '}
            {t('booking:planCheckout.processing', 'Processing…')}
          </span>
        ) : (
          t('booking:planCheckout.payButton', 'Subscribe and book — {{amount}} / month', {
            amount: formatCurrency(init.amount, init.currency),
          })
        )}
      </button>
    </form>
  );
};

export default PlanCheckoutScreen;
